import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { CropperRef } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
// eslint-disable-next-line import/no-extraneous-dependencies
import { styled } from '@mui/material/styles'
import { Button, IconButton, Slider, Typography } from '@mui/material'
import { useFileObjectUrl } from '$func/file'
import { MediaCard } from '$common/media-card'
import { AppBarBackTitleActionMenu } from '$common/app-bar/app-bar-back-title-action-menu'
import { RotateIcon } from '$common/icons/rotate'
import { FlipIcon } from '$common/icons/flip'
import { ImagePreviewTypes } from '$comp/_app/components/chat/components/footer/components/send-field/send-field.type'
import {
  BrightnessIcon,
  ContrastIcon,
  HueIcon,
  SaturationIcon,
} from '$common/icons'
import { ConfirmCancelBox } from '$comp/_app/components/chat/components/footer/components/send-field/components/preview-button-bar/confirm-cancel-box'
import { toLocale } from '$func/text'
import { nothing } from '$func/utils'
import { t } from '$i18n'
import { CircleStencil } from '$comp/_app/components/chat/components/footer/components/send-field/components/upload-preview/image-preview/cropper/circle-stencil'
import { Cropper } from './cropper/cropper'
import './image-preview.scss'

const APPBAR_HEIGHT = 50

const StyledImage = styled(`img`)`
  width: 100%;
  position: absolute;
  object-fit: contain;
  z-index: -1;
`

interface Props {
  file: File
  onCancel: () => unknown
  imagePreviewMode?: ImagePreviewTypes
  setImagePreviewMode?: Dispatch<SetStateAction<ImagePreviewTypes>>
  setFile: (newFile: File) => unknown
  onResetCb?: () => unknown
  stencilType?: 'DEFAULT' | 'CIRCULAR'
}

const baseValue = 0
function TunePanelRow({
  edge,
  icon,
  label,
  value,
  onChange,
}: {
  edge: number
  label: string
  icon: JSX.Element
  value: number
  onChange: (newVal: number) => unknown
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <Typography
          variant="caption1"
          sx={{ marginLeft: 'auto' }}
          color="white"
        >
          {value ? toLocale(-value) : toLocale(0)}
        </Typography>
        <Typography variant="caption1" color="white">
          {label}
        </Typography>
        {icon}
      </div>
      <Slider
        sx={{
          padding: '4px 0',
          '& .MuiSlider-thumb': {
            width: '12px',
            height: '12px',
            display: value > 0 ? 'none' : 'unset',
            color: 'white',
            transform: 'translate(+50%,-50%)',
            '&+.MuiSlider-thumb': {
              display: value > 0 ? 'flex' : 'none',
              // color: value > 0 ? 'unset' : 'white',
            },
          },
          '& .MuiSlider-track': {
            color: 'white',
          },
        }}
        min={-edge}
        max={edge}
        value={[
          value < baseValue ? value : baseValue,
          value > baseValue ? value : baseValue,
        ]}
        onChange={(_, v) => {
          onChange(v[0] !== baseValue ? v[0] : v[1])
        }}
      />
    </div>
  )
}

interface Adjustments {
  brightness: number
  hue: number
  saturation: number
  contrast: number
}

function TunePanel({
  adjustments,
  updateAdjustments,
}: {
  adjustments: Adjustments
  updateAdjustments: (type: keyof Adjustments, value: number) => unknown
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        gap: '16px',
      }}
    >
      <TunePanelRow
        edge={100}
        label={t('sendField.brightness')}
        icon={<BrightnessIcon />}
        value={-adjustments.brightness}
        onChange={val => updateAdjustments('brightness', -val)}
      />
      <TunePanelRow
        edge={100}
        label={t('sendField.contrast')}
        icon={<ContrastIcon />}
        value={-adjustments.contrast}
        onChange={val => updateAdjustments('contrast', -val)}
      />
      <TunePanelRow
        edge={100}
        label={t('sendField.saturation')}
        icon={<SaturationIcon />}
        value={-adjustments.saturation}
        onChange={val => updateAdjustments('saturation', -val)}
      />
      <TunePanelRow
        edge={100}
        label={t('sendField.hue')}
        icon={<HueIcon />}
        value={-adjustments.hue}
        onChange={val => updateAdjustments('hue', -val)}
      />
    </div>
  )
}

function CropPanel({ cropperRef }: { cropperRef: MutableRefObject<any> }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px',
        width: '100%',
        position: 'relative',
        // bottom: '56px',
      }}
    >
      <IconButton onClick={() => cropperRef.current.rotateImage(90)}>
        <RotateIcon />
      </IconButton>
      <Button
        color="secondary"
        variant="text"
        onClick={() => cropperRef.current.reset()}
      >
        {t('sendField.refresh')}
      </Button>
      <IconButton onClick={() => cropperRef.current.flipImage(true, false)}>
        <FlipIcon />
      </IconButton>
    </div>
  )
}

export function ImagePreview({
  file,
  onCancel,
  imagePreviewMode = 'crop',
  setImagePreviewMode = nothing,
  setFile,
  onResetCb = nothing,
  stencilType = 'DEFAULT',
}: Props) {
  const url = useFileObjectUrl(file)
  const [offset, setOffset] = useState<number>()
  const [marginTop, setMarginTop] = useState(APPBAR_HEIGHT)
  const [maxHeight, setMaxHeight] = useState<number>()
  const [width, setWidth] = useState<number>()
  const [loaded, setLoaded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const cropperRef = useRef<CropperRef>()

  if (!file) return null

  const handleLoad = useCallback(() => {
    const card = cardRef.current
    const image = imageRef.current
    if (!card || !image) return

    setOffset(card.clientHeight - image.clientHeight)
    setWidth(cardRef.current?.clientWidth)
    setLoaded(true)
    cropperRef.current?.reset()
  }, [cropperRef])

  useEffect(() => {
    const card = cardRef.current
    if (!card) return
    const margin = offset && offset > 0 ? offset / 2 : 0
    const calculatedMarginTop =
      imagePreviewMode === 'tune'
        ? margin - 212 > 0
          ? margin - 212
          : 0
        : imagePreviewMode === 'crop'
        ? margin - 120 > 0
          ? margin - 120
          : 0
        : margin

    setMarginTop(calculatedMarginTop)

    const max = card.clientHeight

    const calculatedMaxHeight =
      imagePreviewMode === 'tune'
        ? max - 228
        : imagePreviewMode === 'crop'
        ? max - 120
        : max
    setMaxHeight(calculatedMaxHeight)
  }, [offset, imagePreviewMode])

  const [adjustments, setAdjustments] = useState<Adjustments>({
    brightness: 0,
    hue: 0,
    saturation: 0,
    contrast: 0,
  })

  const updateAdjustments = useCallback(
    (type: keyof Adjustments, value: number) => {
      setAdjustments({
        ...adjustments,
        [type]: value,
      })
    },
    [adjustments],
  )

  const onConfirm = async () => {
    const cropper = cropperRef.current
    if (!cropper) return
    const blob = await new Promise<Blob | null | undefined>(resolve => {
      cropper.getCanvas()?.toBlob(resolve, file.type)
    })
    if (!blob) return
    const newFile = new File([blob], file.name, { type: file.type })
    setFile(newFile)
    setImagePreviewMode('comment')
  }
  const onReset = () => {
    const cropper = cropperRef.current
    if (!cropper) return
    cropper.reset()
    setImagePreviewMode('comment')
    onResetCb()
  }

  return (
    <MediaCard
      appbarComponent={
        imagePreviewMode === 'comment' ? (
          <AppBarBackTitleActionMenu
            title={t('chat.sendImage')}
            onBack={onCancel}
          />
        ) : (
          undefined
        )
      }
      cardRef={cardRef}
      sxProps={{
        position: 'fixed',
        top: 0,
      }}
    >
      <>
        {imagePreviewMode === 'comment' && (
          <StyledImage
            src={url}
            ref={imageRef}
            sx={{
              marginTop: `${marginTop}px`,
              visibility: loaded ? 'visible' : 'hidden',
              maxHeight,
            }}
            onLoad={handleLoad}
          />
        )}

        {imagePreviewMode !== 'comment' && url && (
          <div
            className="image-editor"
            style={{ height: maxHeight, width, maxWidth: width }}
          >
            <div className="image-editor__cropper">
              <Cropper
                ref={cropperRef}
                src={url}
                stencilComponent={
                  stencilType === 'CIRCULAR' ? CircleStencil : undefined
                }
              />
            </div>
          </div>
        )}

        {imagePreviewMode === 'tune' && (
          <TunePanel
            adjustments={adjustments}
            updateAdjustments={updateAdjustments}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {imagePreviewMode === 'crop' && <CropPanel cropperRef={cropperRef} />}

          {imagePreviewMode !== 'comment' && (
            <ConfirmCancelBox onConfirm={onConfirm} onReset={onReset} />
          )}
        </div>
      </>
    </MediaCard>
  )
}
